import i18n, { type BackendModule, type ResourceKey } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

// English ships in the entry: it is the fallback every missing key resolves
// to, so it must be there from the first render.
import en from './i18n/bundles/en';

/**
 * Every other language is its own chunk, fetched when a visitor needs it. All
 * eight used to be in the entry (≈2 MB of JSON, parsed by every visit to the
 * landing for the one language shown). The signed-in app registers them all
 * again as it loads (`i18n/all-languages`), so everything behind the landing
 * — field-of-study search across languages, card previews in a deck's own
 * language — sees every language exactly as before.
 */
const LOADERS: Record<string, () => Promise<{ default: ResourceKey }>> = {
  ca: () => import('./i18n/bundles/ca'),
  de: () => import('./i18n/bundles/de'),
  es: () => import('./i18n/bundles/es'),
  fr: () => import('./i18n/bundles/fr'),
  he: () => import('./i18n/bundles/he'),
  it: () => import('./i18n/bundles/it'),
  pl: () => import('./i18n/bundles/pl'),
  sv: () => import('./i18n/bundles/sv'),
};

/** i18next's backend slot, pointed at the chunks above. A regional code
 *  (`it-IT`) finds nothing here and falls through to its base language. */
const lazyLanguages: BackendModule = {
  type: 'backend',
  init() {},
  read(language, namespace, callback) {
    const load = namespace === 'translation' ? LOADERS[language] : undefined;
    if (!load) {
      callback(null, {});
      return;
    }
    // A failed chunk (offline, a deploy in between) leaves the English
    // fallback on screen rather than holding the page.
    load().then(
      (module) => callback(null, module.default),
      (error: Error) => callback(error, null),
    );
  },
};

/** A `/<lang>/…` marketing URL's language, loaded with the first render too:
 *  LangBoundary shows nothing until i18n speaks the URL's language. */
const urlLang =
  typeof window !== 'undefined' ? window.location.pathname.split('/')[1] : '';

/**
 * Resolves once the detected language (and the URL's, if it names another)
 * is loaded. `main.tsx` renders after it, so no component ever mounts with a
 * language still on its way.
 */
export const i18nReady: Promise<unknown> = i18n
  .use(lazyLanguages)
  .use(LanguageDetector) // Detects user language
  .use(initReactI18next) // Passes i18n down to react-i18next
  .init({
    resources: { en: { translation: en } },
    // Use the bundled English and fetch the rest through `lazyLanguages`.
    partialBundledLanguages: true,
    preload: LOADERS[urlLang] ? [urlLang] : [],
    fallbackLng: 'en', // Use English if detected language is not available
    // Both are i18next's OWN defaults, restated explicitly ON PURPOSE — this is
    // a hot-path fix, not cosmetics. Passing either one sets i18next's internal
    // `userDefinedKeySeparator` / `userDefinedNsSeparator`, which short-circuits
    // the `looksLikeObjectPath()` probe it otherwise runs on EVERY `t()` call
    // (a `chars.filter` with two `indexOf`s per char, then a cached regex test).
    // Measured on a 209-deck account: ~26ms of a ~300ms folder-open JS burst was
    // that probe alone. Safe because no key we ship — static or interpolated —
    // contains a ':' (the probe only changes behaviour for keys that do).
    keySeparator: '.',
    nsSeparator: ':',
    interpolation: {
      escapeValue: false, // React already safes from xss
    },
  })
  .catch(() => undefined);

/**
 * Fetch a language before switching to it, so the switch itself is instant:
 * a `/<lang>/…` page switched before its strings arrived would sit blank
 * behind LangBoundary for the length of the fetch.
 */
export function loadLanguage(lang: string): Promise<void> {
  return i18n.loadLanguages(lang).catch(() => undefined);
}

export default i18n;
